import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
  ClientSidePermissionTeamLinkService,
  ClientSidePermissionService,
  ClientSidePermissionRoleLinksAppletLoginService,
  LinkSubjectToTeamService,
  TenantUserRoleService,
  Pagination,
  TeamService,
  ClientSideTenantPermissionService,
  ClientSidePermissionRoleLinksService
} from "blg-akaun-ts-lib";
import { ToastrService } from "ngx-toastr";
import { SessionActions } from "../../../../modules/session/session-controller/actions";
import { SessionSelectors } from "../../../../modules/session/session-controller/selectors";
import { SessionStates } from "../../../../modules/session/session-controller/states";
import { AppConfig } from "../../../../visa";
import { from, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap, toArray, withLatestFrom } from "rxjs/operators";
import { PermissionFacade } from "../../facades/permission.facade";
import { PermissionStates } from "../../permission-controller/root";
import { ClientSidePermissionsActions } from "../actions";
import { ClientSidePermissionsSelectors } from "../selectors";
import { ClientSideViewModel } from "../states/client-side-permission.states";

@Injectable()
export class ClientSidePermissionsEffects {

  loadClientSidePermissions$ = createEffect(() => this.actions$.pipe(
    ofType(
      SessionActions.getUserProfileSuccess,
      ClientSidePermissionsActions.createClientSidePermSuccess,
      ClientSidePermissionsActions.updateClientSidePermSuccess,
      ClientSidePermissionsActions.deleteClientSidePermSuccess,
    ),
    withLatestFrom(this.sessionStore.select(SessionSelectors.selectUserProfile)),
    mergeMap(([action, profile]) => {
      return this.roleService.getByCriteria(new Pagination(
        0, 1000,
        [
          {columnName: "app_subject_guid", operator: "=", value: profile.appLoginSubject.guid.toString()}
        ]
      ), AppConfig.apiVisa).pipe(
        map(response => response.data),
        mergeMap(links => from(links).pipe(
          mergeMap(roleGuid => {
            return this.clientSidePermissionRoleLinksAppletLoginService.getByCriteriLoginEp(sessionStorage.getItem('appletGuid'),
              new Pagination(
              0, 1000,
              [
                {columnName: 'role_guid', operator: '=', value: roleGuid.link_subject_to_role.app_mst_role_guid.toString()},
              ]
            ), AppConfig.apiVisa).pipe(
              map(result => result.data),
              mergeMap(links => from(links).pipe(
                mergeMap(link => {
                  return this.clientSidePermissionService.getByGuid(link.bl_applet_client_side_perm_to_role_link.client_perm_guid.toString(), AppConfig.apiVisa).pipe(
                    map(result => result.data)
                  )
                }),
              )),
            )
          }),
        )),
        toArray(),
        map(a => a.map(p => (<ClientSideViewModel>{
          guid: p.bl_applet_client_side_perm_dfn.guid,
          perm_code: p.bl_applet_client_side_perm_dfn.perm_code
        }))),
        map(response => {return ClientSidePermissionsActions.clientSidePermissionsSuccess({ permissions: response })}),
        catchError(error => of(ClientSidePermissionsActions.clientSidePermissionsFailed({ error })))
      )
    }),
  ));

  createClientSidePerm$ = createEffect(() => this.actions$.pipe(
    ofType(ClientSidePermissionsActions.createClientSidePermInit),
    mergeMap(action => this.clientSidePermissionRoleLinksService.post(action.clientSidePerm, AppConfig.apiVisa)
      .pipe(
        map(_ => {
          this.displayToasterSuccess('Client Side Permission created successfully');
          //this.onReturn();
          return ClientSidePermissionsActions.createClientSidePermSuccess();
        }),
        catchError((err) => {
          this.displayToasterError(err.message);
          return of(ClientSidePermissionsActions.createClientSidePermFailure({ error: err.message }))
        })
      )),
    catchError((err) => {
      this.displayToasterError(err.message);
      return of(ClientSidePermissionsActions.createClientSidePermFailure({ error: err.message }))
    })
  ));

  deleteClientSidePerm$ = createEffect(() => this.actions$.pipe(
    ofType(ClientSidePermissionsActions.deleteClientSidePermInit),
    withLatestFrom(this.store.select(ClientSidePermissionsSelectors.selectEntity)),
    exhaustMap(([action, entity]) => this.clientSidePermissionRoleLinksService.delete(entity.bl_applet_client_side_perm_to_role_link.guid.toString(), AppConfig.apiVisa)
      .pipe(
        map(_ => {
          this.displayToasterSuccess('Client Side Permission deleted successfully');
          this.onReturn();
          return ClientSidePermissionsActions.deleteClientSidePermSuccess();
        }),
        catchError((err) => {
          this.displayToasterError(err.message);
          return of(ClientSidePermissionsActions.deleteClientSidePermFailure({ error: err.message }))
        })
      )),
    catchError((err) => {
      this.displayToasterError(err.message);
      return of(ClientSidePermissionsActions.deleteClientSidePermFailure({ error: err.message }))
    })
  ));


  updateClientSidePerm$ = createEffect(() => this.actions$.pipe(
    ofType(ClientSidePermissionsActions.updateClientSidePermInit),
    withLatestFrom(this.store.select(ClientSidePermissionsSelectors.selectEntity)),
    exhaustMap(([action, entity]) => this.clientSidePermissionRoleLinksService.getByGuid(entity.bl_applet_client_side_perm_to_role_link.guid.toString(), AppConfig.apiVisa).pipe(
      map(a => {
        a.data.bl_applet_client_side_perm_to_role_link.client_perm_guid = action.clientSidePerm.bl_applet_client_side_perm_to_role_link.client_perm_guid;
        a.data.bl_applet_client_side_perm_to_role_link.role_guid = action.clientSidePerm.bl_applet_client_side_perm_to_role_link.role_guid;
        return a.data;
      }),
      exhaustMap(b => this.clientSidePermissionRoleLinksService.put(b, AppConfig.apiVisa).pipe(
        map(_ => {
          this.displayToasterSuccess('Client Side Permission updated successfully');
          this.onReturn();
          return ClientSidePermissionsActions.updateClientSidePermSuccess();
        }),
        catchError((err) => {
          this.displayToasterError(err.message);
          return of(ClientSidePermissionsActions.updateClientSidePermFailure({ error: err.message }));
        })
      )),
      catchError((err) => {
        this.displayToasterError(err.message);
        return of(ClientSidePermissionsActions.updateClientSidePermFailure({ error: err.message }));
      })
    ))
  ));

  createTenantClientSidePerm$ = createEffect(() => this.actions$.pipe(
    ofType(ClientSidePermissionsActions.createTenantClientSidePermInit),
    mergeMap(action => this.tenantClientSidePermissionService.post(action.clientSidePerm, AppConfig.apiVisa)
      .pipe(
        map(_ => {
          return ClientSidePermissionsActions.createTenantClientSidePermSuccess();
        }),
        catchError((err) => {
          return of(ClientSidePermissionsActions.createTenantClientSidePermFailure({ error: err.message }))
        })
      )),
    catchError((err) => {
      return of(ClientSidePermissionsActions.createTenantClientSidePermFailure({ error: err.message }))
    })
  ));
  prevIndex: number;
  prevLocalState: any;

  constructor(
    private actions$: Actions,
    private permFacade: PermissionFacade,
    private teamService: LinkSubjectToTeamService,
    private roleService: TenantUserRoleService,
    private clientSidePermissionRoleLinksAppletLoginService: ClientSidePermissionRoleLinksAppletLoginService,
    private clientSidePermissionGroupLinkService: ClientSidePermissionTeamLinkService,
    private clientSidePermissionRoleLinksService:ClientSidePermissionRoleLinksService,
    private clientSidePermissionService: ClientSideTenantPermissionService,
    private tenantClientSidePermissionService: ClientSideTenantPermissionService,
    private toaster: ToastrService,
    private readonly store: Store<PermissionStates>,
    private readonly sessionStore: Store<SessionStates>,
  ) {
    this.permFacade.prevIndex$.subscribe(
      (resolve) => (this.prevIndex = resolve)
    );
    this.permFacade
      .prevLocalState$()
      .subscribe((resolve) => (this.prevLocalState = resolve));
  }

  /**
     * Display a toaster when there is an error.
     *
     * @param message : the messsage to display
     * @returns the toaster
     */
  displayToasterError(message: string) {
    return this.toaster.error(
      message,
      'Error',
      {
        tapToDismiss: true,
        progressBar: true,
        timeOut: 1300
      }
    )
  }

  /**
     * Display a successful toaster message.
     *
     * @param message : the message to display
     * @returns the toaster
     */
  displayToasterSuccess(message: string) {
    return this.toaster.success(
      message,
      'Success',
      {
        tapToDismiss: true,
        progressBar: true,
        timeOut: 1300
      }
    )
  }

  onReturn() {
    this.permFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateAdd: false,
      deactivateList: false,
    });
    console.log(" Previous Index applet ", this.prevIndex);
    this.permFacade.onPrev(this.prevIndex);
  }

}
